import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom"
import { useEffect, useRef } from "react"
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import * as Sentry from '@sentry/react'
import { Toaster } from "@/components/ui/sonner"
import { AuthProvider, useAuth, AuthLoadingScreen } from "@/contexts/AuthContext"
import { isSuiviSeul, markPushNotificationRead } from "@/lib/supabase"
import { authUrlReturningTo } from "@/lib/trialLink"
import { AuthPage } from "@/components/AuthPage"
import { DiagnosticFlow } from "@/diagnostic/DiagnosticFlow"
import { DashboardHome } from "@/components/DashboardHome"
import { DashboardPage } from "@/components/DashboardPage"
import { ProgressPage } from "@/components/ProgressPage"
import { NutritionPage } from "@/components/NutritionPage"
import { WeeklyMealPlan } from "@/components/WeeklyMealPlan"
import { HooperForm } from "@/components/HooperForm"
import { PostSessionForm } from "@/components/monitoring/PostSessionForm"
import SessionPage from "@/components/SessionPage"
import { CalendarPage } from "@/components/CalendarPage"
import { ProfilePage } from "@/components/ProfilePage"
import { ProgramSettingsPage } from "@/components/profile/ProgramSettingsPage"
import { SettingsPage } from "@/components/profile/SettingsPage"
import { RecordHistoryPage } from "@/components/profile/RecordHistoryPage"
import { SessionsHistoryPage } from "@/components/SessionsHistoryPage"
import { NotificationsPage } from "@/components/NotificationsPage"
import { BillingPage } from "@/components/BillingPage"
import { TrialReportPage } from "@/components/TrialReportPage"
import { RequireSubscription } from "@/components/RequireSubscription"
import { RequireAdmin } from "@/components/RequireAdmin"
import { AdminUsersPage } from "@/components/admin/AdminUsersPage"
import { AdminUserDetailPage } from "@/components/admin/AdminUserDetailPage"
import { AdminStatsPage } from "@/components/admin/AdminStatsPage"
import { ConsentBanner } from "@/components/ConsentBanner"
import { PrivacyPage } from "@/components/PrivacyPage"
import { useAppUpdate } from "@/hooks/useAppUpdate"
import { hasRecentSessionInProgress } from "@/lib/sessionResume"

/**
 * Routes internes — rendues à l'intérieur de AuthProvider.
 *
 * FIX FLASH WIZARD :
 * Après un SIGN_IN, wizardLoading passe à true pendant que fetchWizardData tourne.
 * Si on ne l'attend pas, hasWizard=false pendant ~500ms → affichage bref du wizard.
 * → On affiche AuthLoadingScreen tant que wizardLoading est true ET user vient d'être set.
 *
 * ⚠️ AuthLoadingScreen REMPLACE la page : tout ce qu'elle a en mémoire est perdu. Depuis
 * le correctif « reprise au retour sur l'onglet », wizardLoading ne passe à true que pour
 * un vrai changement de compte (ou un `refreshWizardData` explicite). Les revérifications
 * de session de Supabase (SIGNED_IN à chaque retour sur l'onglet, TOKEN_REFRESHED) relisent
 * le diagnostic en arrière-plan, sans démonter la page — voir AuthContext.
 */
function AppRoutes() {
  const { wizardData, user, loading, wizardLoading, hasWizard, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Nouvelle version déployée → rechargement au changement d'écran suivant.
  useAppUpdate()

  // Clic sur une notification push, app déjà ouverte : le service worker envoie
  // NAVIGATE. L'entrée de cloche correspondante passe en « lue » — par son
  // identifiant (send-push le transmet depuis le 04/10), sinon par son type.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'NAVIGATE' || typeof event.data.url !== 'string') return
      const notificationId: string = event.data.notification_id ?? ''
      const pushType: string = event.data.push_type ?? ''
      navigate(event.data.url)
      if (notificationId || pushType) {
        void markPushNotificationRead(notificationId || null, pushType || null)
      }
    }
    navigator.serviceWorker?.addEventListener('message', handler)
    return () => navigator.serviceWorker?.removeEventListener('message', handler)
  }, [navigate])

  // Clic sur une notification push, app fermée : le service worker ouvre l'URL avec
  // ?push_type=…&notification_id=… — retirés de l'URL aussitôt, les autres
  // paramètres sont conservés.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const pushType = params.get('push_type')
    const notificationId = params.get('notification_id')
    if (!pushType && !notificationId) return
    params.delete('push_type')
    params.delete('notification_id')
    const rest = params.toString()
    window.history.replaceState({}, '', location.pathname + (rest ? `?${rest}` : ''))
    void markPushNotificationRead(notificationId, pushType)
  }, [location.search, location.pathname])

  // Démarrage à froid au milieu d'une séance : iOS ferme l'app installée en arrière-plan
  // (le temps d'ouvrir Spotify peut suffire) et la rouvre sur start_url, /dashboard. Une
  // séance qui a avancé il y a peu reprend d'office (voir lib/sessionResume). Une seule
  // fois par démarrage : revenir ensuite à l'accueil depuis la séance reste possible.
  const resumeChecked = useRef(false)
  useEffect(() => {
    if (resumeChecked.current || loading || wizardLoading || !user || !hasWizard) return
    resumeChecked.current = true
    if (location.pathname === "/dashboard" && !isSuiviSeul(wizardData) && hasRecentSessionInProgress(user.id)) {
      navigate("/dashboard/session")
    }
  }, [loading, wizardLoading, user, hasWizard, wizardData, location.pathname, navigate])

  // Hydratation initiale
  if (loading) return <AuthLoadingScreen />

  const isOnAuthPage = window.location.pathname === "/auth"
  if (isAuthenticated && wizardLoading && !isOnAuthPage) {
    return <AuthLoadingScreen />
  }

  const sessionProps = wizardData
    ? {
        email: wizardData.email,
        firstName: wizardData.first_name,
        offerId: wizardData.offer_id ?? undefined,
        goalId: wizardData.goal_id,
        levelId: wizardData.level_id,
      }
    : null

  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />

      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : hasWizard ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <DiagnosticFlow />
          )
        }
      />

      <Route
        path="/dashboard"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <DashboardHome
              email={sessionProps.email}
              firstName={sessionProps.firstName}
              offerId={sessionProps.offerId}
              goalId={sessionProps.goalId}
            />
          )
        }
      />

      <Route
        path="/dashboard/session"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : isSuiviSeul(wizardData) ? (
            // Suivi seul : pas de séance Zeyone à prévisualiser ni à lancer (lien direct,
            // ancien push, reprise de séance) — retour à l'accueil, qui propose d'en ajouter une.
            <Navigate to="/dashboard" replace />
          ) : (
            <RequireSubscription feature="Accès aux séances">
              <SessionPage />
            </RequireSubscription>
          )
        }
      />

      <Route
        path="/dashboard/monitoring"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <RequireSubscription feature="Monitoring scientifique">
              <DashboardPage
                email={sessionProps.email}
                firstName={sessionProps.firstName}
                offerId={sessionProps.offerId}
              />
            </RequireSubscription>
          )
        }
      />

      <Route
        path="/dashboard/progress"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <RequireSubscription feature="Suivi de progression">
              <ProgressPage
                email={sessionProps.email}
                firstName={sessionProps.firstName}
                goalId={sessionProps.goalId}
              />
            </RequireSubscription>
          )
        }
      />

      <Route
        path="/dashboard/nutrition"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <RequireSubscription feature="Plans nutrition personnalisés">
              <NutritionPage
                email={sessionProps.email}
                firstName={sessionProps.firstName}
                goalId={sessionProps.goalId}
              />
            </RequireSubscription>
          )
        }
      />

      <Route
        path="/dashboard/nutrition/week"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <RequireSubscription feature="Semaine type alimentaire">
              <WeeklyMealPlan goalId={sessionProps.goalId} />
            </RequireSubscription>
          )
        }
      />

      <Route
        path="/dashboard/calendar"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <CalendarPage
              email={sessionProps.email}
              firstName={sessionProps.firstName}
            />
          )
        }
      />

      <Route
        path="/dashboard/profile"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <ProfilePage
              email={sessionProps.email}
              firstName={sessionProps.firstName}
              goalId={sessionProps.goalId}
            />
          )
        }
      />

      <Route
        path="/dashboard/profile/program"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <ProgramSettingsPage />
          )
        }
      />

      <Route
        path="/dashboard/profile/settings"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <SettingsPage />
          )
        }
      />

      <Route
        path="/dashboard/profile/records/:movement"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <RecordHistoryPage />
          )
        }
      />

      <Route
        path="/dashboard/sessions"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <SessionsHistoryPage
              email={sessionProps.email}
              firstName={sessionProps.firstName}
            />
          )
        }
      />

      <Route
        path="/dashboard/notifications"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <NotificationsPage />
          )
        }
      />

      <Route
        path="/dashboard/billing"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <BillingPage />
          )
        }
      />

      {/* Fin de la semaine d'essai : bilan des 7 jours, puis les 4 offres.
          Volontairement HORS RequireSubscription — c'est l'écran qu'on voit
          justement quand on n'a plus accès.
          Déconnecté, on passe par la connexion AVEC le retour ici : l'email du
          matin de J8 s'ouvre souvent sur un téléphone non connecté (chantier C). */}
      <Route
        path="/fin-essai"
        element={
          !isAuthenticated ? (
            <Navigate to={authUrlReturningTo(location.pathname, location.search)} replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <TrialReportPage />
          )
        }
      />

      <Route
        path="/hooper"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <HooperForm email={sessionProps.email} />
          )
        }
      />

      <Route
        path="/post-session"
        element={
          !isAuthenticated ? (
            <Navigate to="/auth" replace />
          ) : !sessionProps ? (
            <Navigate to="/" replace />
          ) : (
            <PostSessionForm email={sessionProps.email} />
          )
        }
      />

      <Route
        path="/admin/users"
        element={
          <RequireAdmin>
            <AdminUsersPage />
          </RequireAdmin>
        }
      />

      <Route
        path="/admin/users/:id"
        element={
          <RequireAdmin>
            <AdminUserDetailPage />
          </RequireAdmin>
        }
      />

      <Route
        path="/admin/stats"
        element={
          <RequireAdmin>
            <AdminStatsPage />
          </RequireAdmin>
        }
      />

      <Route path="/privacy" element={<PrivacyPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function ErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div className="min-h-screen bg-[#060a13] text-gray-100 flex items-center justify-center px-5">
      <div className="w-full max-w-md bg-gray-900/70 border border-gray-800/60 rounded-2xl p-6 text-center">
        <h1 className="text-[20px] font-bold text-white mb-2">Une erreur est survenue</h1>
        <p className="text-[13px] text-gray-400 mb-6 leading-relaxed">
          On a été notifiés et on regarde ça. Tu peux réessayer ou revenir à l'accueil.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={resetError}
            className="w-full py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer"
            style={{ background: 'linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)' }}
          >
            Réessayer
          </button>
          <button
            onClick={() => { window.location.href = '/' }}
            className="w-full py-3 rounded-xl text-[13px] font-semibold text-gray-300 cursor-pointer bg-gray-800/40 border border-gray-700/40"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <PostHogProvider client={posthog}>
        <AuthProvider>
          <AppRoutes />
          <ConsentBanner />
          <Toaster />
        </AuthProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
